import React, { useEffect, useState } from 'react';
import api from 'config/axiosConfig';
import { apiRoutes } from "util/apiRoutes";

export interface DimensionData {
  dimension: string;
  dimensionLabel: string;
  label1: string;
  label2: string;
  targetValue: number | null;
  currentValue: number | null;
  alignment: number | null;
  // Percent split for the viewed user/team on each side of the dimension
  targetLabel1Pct?: number;
  targetLabel2Pct?: number;
}

export interface AlignmentCalculation {
  similariteaPercentage: number;
  pureAlignmentPercentage: number;
  dimensionData: DimensionData[];
  similarTopics: number;
  differentTopics: number;
  mutuallyImportantTopics: number;
  collaborationStatements: AlignmentStatement[];
  engagementStatements: AlignmentStatement[];
}

export interface AlignmentStatement {
  id: number;
  statement: string;
  currentUserImportance: number;
  targetUserImportance: number;
  agreement: number;
}

interface AlignmentDataCalculatorProps {
  targetUsers: any[]; // Array of users to aggregate data for (can be User or UserWithPublicProps)
  currentUser: any | null; // Can be User or UserWithPublicProps
  onDataCalculated: (data: AlignmentCalculation) => void;
  isTeam?: boolean;
  statementFilterIds?: number[] | null;
}

// Define all dimensions based on CreateTeabate.tsx
const ALIGNMENT_DIMENSIONS = [
  { key: "nationalism_cosmopolitanism", dimension: "Diplomacy", label1: "Nationalism", label2: "Cosmopolitanism" },
  { key: "theocracy_secularism", dimension: "Religion", label1: "Theocracy", label2: "Secularism" },
  { key: "authority_liberty", dimension: "State", label1: "Authority", label2: "Liberty" },
  { key: "capitalism_socialism", dimension: "Economics", label1: "Capitalism", label2: "Socialism" },
  { key: "dogma_science", dimension: "Beliefs", label1: "Dogma", label2: "Science" },
  { key: "autocracy_democracy", dimension: "Government", label1: "Autocracy", label2: "Democracy" },
  { key: "conservatism_progressivism", dimension: "Traditions", label1: "Conservatism", label2: "Progressivism" },
  { key: "content_discontent", dimension: "Sentiment", label1: "Status Quo", label2: "Reformation" }
];

const ALIGNMENT_WEIGHT = 0.6;
const IMPORTANCE_WEIGHT = 0.4;

const AlignmentDataCalculator: React.FC<AlignmentDataCalculatorProps> = ({
  targetUsers,
  currentUser,
  onDataCalculated,
  isTeam = false,
  statementFilterIds = null,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (targetUsers.length === 0 || !currentUser) {
      onDataCalculated({
        similariteaPercentage: 0,
        pureAlignmentPercentage: 0,
        dimensionData: [],
        similarTopics: 0,
        differentTopics: 0,
        mutuallyImportantTopics: 0,
        collaborationStatements: [],
        engagementStatements: []
      });
      return;
    }

    fetchAndCalculateAlignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUsers, currentUser, statementFilterIds]);

  const fetchAndCalculateAlignments = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch alignments for all target users
      const targetUserAlignments = await Promise.all(
        targetUsers.map(user => fetchUserAlignments(user.id))
      );

      // Fetch current user alignments
      const currentUserAlignments = await fetchUserAlignments(currentUser!.id);

      // Calculate aggregated alignment data
      const calculatedData = calculateAlignmentData(
        targetUserAlignments,
        currentUserAlignments
      );

      onDataCalculated(calculatedData);
    } catch (err) {
      console.error('Error fetching alignment data:', err);
      setError('Failed to load alignment data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserAlignments = async (userId: number) => {
    try {
      const response = await api.get(apiRoutes.user.userAlignments(userId));
      const relations = response.data.relations || [];
      const statementFilterIdSet = statementFilterIds && statementFilterIds.length > 0
        ? new Set(statementFilterIds.map((value) => Number(value)))
        : null;
      
      // Fetch teabate details for each relation
      const teabatePromises = relations.map((rel: any) =>
        api.get(apiRoutes.posts.byId(rel.rel_node_id)).catch(() => null)
      );
      
      const teabateResponses = await Promise.all(teabatePromises);
      
      // Combine relations with teabate details
      return relations.map((rel: any, index: number) => {
        const teabateResponse = teabateResponses[index];
        return {
          relation: rel,
          teabate: teabateResponse?.data || null
        };
      }).filter((item: any) => {
        if (!item.teabate || !item.teabate.properties.dimension || item.teabate.properties.dimension === 'none') {
          return false;
        }
        if (!statementFilterIdSet) {
          return true;
        }
        return statementFilterIdSet.has(Number(item.teabate.id));
      });
    } catch (error) {
      console.error(`Error fetching alignments for user ${userId}:`, error);
      return [];
    }
  };

  // Note: We no longer use normalizeValue since we operate on sided percentages.

  const calculateAlignmentData = (
    targetUserAlignments: any[][],
    currentUserAlignments: any[]
  ): AlignmentCalculation => {
    const dimensionScores: Record<string, {
      // Net values used for polygon (-1..1 after normalization)
      targetSum: number;
      targetCount: number;
      currentSum: number;
      currentCount: number;
      // Raw sided totals used to compute percentages per side
      targetSide1: number; // weight toward label1
      targetSide2: number; // weight toward label2
      currentSide1: number;
      currentSide2: number;
      // Agreement scoring for per-dimension similaritea
      matchScore: number;
      maxPossible: number;
      targetImportanceSum: number;
      targetImportanceCount: number;
      currentImportanceSum: number;
      currentImportanceCount: number;
    }> = {};

    // Initialize dimension scores
    ALIGNMENT_DIMENSIONS.forEach(dim => {
      dimensionScores[dim.key] = {
        targetSum: 0,
        targetCount: 0,
        currentSum: 0,
        currentCount: 0,
        targetSide1: 0,
        targetSide2: 0,
        currentSide1: 0,
        currentSide2: 0,
        matchScore: 0,
        maxPossible: 0,
        targetImportanceSum: 0,
        targetImportanceCount: 0,
        currentImportanceSum: 0,
        currentImportanceCount: 0
      };
    });

    let agreementCount = 0;
    let disagreementCount = 0;
    let mutualImportantCount = 0;

    const collaborationStatements: AlignmentStatement[] = [];
    const engagementStatements: AlignmentStatement[] = [];

    // Process current user alignments
    const currentUserMap = new Map();
    currentUserAlignments.forEach(item => {
      const teabateId = item.teabate.id;
      const dimension = item.teabate.properties.dimension;
      const response = item.relation.properties.response;
      const importance = item.relation.properties.importance || 0;

      currentUserMap.set(teabateId, { response, importance, dimension, teabate: item.teabate });
      
      // Update dimension scores for current user
      const dimKey = getDimensionKey(dimension);
      if (dimKey && dimensionScores[dimKey]) {
        // Split the response into sided weights (0..2)
        const agreeWeight = Math.max(0, response);
        const opposeWeight = Math.max(0, -response);
        if (isFirstDimensionValue(dimension)) {
          // dimension value corresponds to label1
          dimensionScores[dimKey].currentSide1 += agreeWeight;
          dimensionScores[dimKey].currentSide2 += opposeWeight;
        } else {
          // dimension value corresponds to label2
          dimensionScores[dimKey].currentSide2 += agreeWeight;
          dimensionScores[dimKey].currentSide1 += opposeWeight;
        }
        // Net value for polygon: favor label2 positive, label1 negative -> range [-1,1]
        const net = (dimensionScores[dimKey].currentSide2 - dimensionScores[dimKey].currentSide1) / 2; // [-1,1] scale
        dimensionScores[dimKey].currentSum = net;
        dimensionScores[dimKey].currentCount = 1; // we keep net per user, count as 1 to avoid averaging twice
        dimensionScores[dimKey].currentImportanceSum += importance;
        dimensionScores[dimKey].currentImportanceCount += 1;
      }
    });

    // Process target users alignments (aggregate for teams)
    const targetAggregatedMap = new Map();
    
    targetUserAlignments.forEach(userAlignments => {
      userAlignments.forEach(item => {
        const teabateId = item.teabate.id;
        const dimension = item.teabate.properties.dimension;
        const response = item.relation.properties.response;
        const importance = item.relation.properties.importance || 0;

        if (!targetAggregatedMap.has(teabateId)) {
          targetAggregatedMap.set(teabateId, {
            responses: [],
            importances: [],
            dimension,
            teabate: item.teabate
          });
        }
        
        targetAggregatedMap.get(teabateId).responses.push(response);
        targetAggregatedMap.get(teabateId).importances.push(importance);
      });
    });

    // Calculate aggregated values and alignment scores
    targetAggregatedMap.forEach((targetData, teabateId) => {
      const currentData = currentUserMap.get(teabateId);
      
      // Calculate average response and importance for target users
      const avgTargetResponse = targetData.responses.reduce((a: number, b: number) => a + b, 0) / targetData.responses.length;
      const avgTargetImportance = targetData.importances.reduce((a: number, b: number) => a + b, 0) / targetData.importances.length;
      
      // Update dimension scores for target
      const dimKey = getDimensionKey(targetData.dimension);
      if (dimKey && dimensionScores[dimKey]) {
        // Split averaged response to sided weights
        const agreeWeight = Math.max(0, avgTargetResponse);
        const opposeWeight = Math.max(0, -avgTargetResponse);
        if (isFirstDimensionValue(targetData.dimension)) {
          dimensionScores[dimKey].targetSide1 += agreeWeight;
          dimensionScores[dimKey].targetSide2 += opposeWeight;
        } else {
          dimensionScores[dimKey].targetSide2 += agreeWeight;
          dimensionScores[dimKey].targetSide1 += opposeWeight;
        }
        const net = (dimensionScores[dimKey].targetSide2 - dimensionScores[dimKey].targetSide1) / 2; // [-1,1]
        dimensionScores[dimKey].targetSum = net;
        dimensionScores[dimKey].targetCount = 1;
        dimensionScores[dimKey].targetImportanceSum += avgTargetImportance;
        dimensionScores[dimKey].targetImportanceCount += 1;
      }
      
      // Calculate alignment if current user has responded
      if (currentData) {
        const agreementLevel = 5 - Math.abs(avgTargetResponse - currentData.response);
        
        if (dimKey && dimensionScores[dimKey]) {
          dimensionScores[dimKey].matchScore += agreementLevel;
          dimensionScores[dimKey].maxPossible += 5;
        }
        
        if (agreementLevel >= 3) {
          agreementCount++;
          
          if (avgTargetImportance > 0 && currentData.importance > 0) {
            mutualImportantCount++;
            collaborationStatements.push({
              id: teabateId,
              statement: targetData.teabate.title,
              currentUserImportance: currentData.importance,
              targetUserImportance: avgTargetImportance,
              agreement: agreementLevel
            });
          }
        } else {
          disagreementCount++;
          
          if (currentData.importance > 0) {
            engagementStatements.push({
              id: teabateId,
              statement: targetData.teabate.title,
              currentUserImportance: currentData.importance,
              targetUserImportance: avgTargetImportance,
              agreement: agreementLevel
            });
          }
        }
      }
    });

    const mutualDimensionScores = ALIGNMENT_DIMENSIONS.map((dim) => {
      const data = dimensionScores[dim.key];
      if (
        data.targetCount === 0
        || data.currentCount === 0
        || data.targetImportanceCount === 0
        || data.currentImportanceCount === 0
      ) {
        return null;
      }

      const responseDifference = Math.min(2, Math.abs(data.targetSum - data.currentSum));
      const responseSimilarity = 100 - ((responseDifference / 2) * 100);

      const avgTargetImportance = data.targetImportanceSum / data.targetImportanceCount;
      const avgCurrentImportance = data.currentImportanceSum / data.currentImportanceCount;
      const importanceDifference = Math.min(4, Math.abs(avgTargetImportance - avgCurrentImportance));
      const importanceSimilarity = 100 - ((importanceDifference / 4) * 100);

      return {
        key: dim.key,
        responseSimilarity,
        importanceSimilarity,
        similaritea: (responseSimilarity * ALIGNMENT_WEIGHT) + (importanceSimilarity * IMPORTANCE_WEIGHT),
      };
    }).filter((entry): entry is {
      key: string;
      responseSimilarity: number;
      importanceSimilarity: number;
      similaritea: number;
    } => entry !== null);

    const alignmentPercentage = mutualDimensionScores.length > 0
      ? mutualDimensionScores.reduce((sum, score) => sum + score.responseSimilarity, 0) / mutualDimensionScores.length
      : 0;

    const similaritea = mutualDimensionScores.length > 0
      ? mutualDimensionScores.reduce((sum, score) => sum + score.similaritea, 0) / mutualDimensionScores.length
      : 0;

    // Prepare dimension data
    const dimensionData: DimensionData[] = ALIGNMENT_DIMENSIONS.map(dim => {
      const data = dimensionScores[dim.key];
      // Percentages for viewed user (target)
      const targetTotal = data.targetSide1 + data.targetSide2;
      const targetLabel1Pct = targetTotal > 0 ? Math.round((data.targetSide1 / targetTotal) * 100) : 0;
      const targetLabel2Pct = targetTotal > 0 ? Math.round((data.targetSide2 / targetTotal) * 100) : 0;

      // Net values for polygons derived from percentages: label2 minus label1 in [-1,1]
      const targetValue = targetTotal > 0 ? (targetLabel2Pct - targetLabel1Pct) / 100 : null;

      const currentTotal = data.currentSide1 + data.currentSide2;
      const currentValue = currentTotal > 0 ? ( (data.currentSide2 / currentTotal) - (data.currentSide1 / currentTotal) ) : null;
      const mutualDimensionScore = mutualDimensionScores.find((score) => score.key === dim.key);
      const alignment = mutualDimensionScore
        ? Math.round(mutualDimensionScore.responseSimilarity)
        : null;

      return {
        dimension: dim.key,
        dimensionLabel: dim.dimension,
        label1: dim.label1,
        label2: dim.label2,
        targetValue,
        currentValue,
        alignment,
        targetLabel1Pct,
        targetLabel2Pct
      };
    });

    // Sort and limit statements
    const sortedCollaborationStatements = collaborationStatements
      .sort((a, b) => (b.currentUserImportance + b.targetUserImportance) - (a.currentUserImportance + a.targetUserImportance))
      .slice(0, 3);
    
    const sortedEngagementStatements = engagementStatements
      .sort((a, b) => b.currentUserImportance - a.currentUserImportance)
      .slice(0, 3);

    return {
      similariteaPercentage: Math.round(similaritea),
      pureAlignmentPercentage: Math.round(alignmentPercentage),
      dimensionData,
      similarTopics: agreementCount,
      differentTopics: disagreementCount,
      mutuallyImportantTopics: mutualImportantCount,
      collaborationStatements: sortedCollaborationStatements,
      engagementStatements: sortedEngagementStatements
    };
  };

  const getDimensionKey = (dimension: string): string | null => {
    // Map dimension values to dimension keys
    const dimensionMap: Record<string, string> = {
      'nationalism': 'nationalism_cosmopolitanism',
      'cosmopolitanism': 'nationalism_cosmopolitanism',
      'theocracy': 'theocracy_secularism',
      'secularism': 'theocracy_secularism',
      'authority': 'authority_liberty',
      'liberty': 'authority_liberty',
      'capitalism': 'capitalism_socialism',
      'socialism': 'capitalism_socialism',
      'dogma': 'dogma_science',
      'science': 'dogma_science',
      'autocracy': 'autocracy_democracy',
      'democracy': 'autocracy_democracy',
      'conservatism': 'conservatism_progressivism',
      'progressivism': 'conservatism_progressivism',
      'content': 'content_discontent',
      'discontent': 'content_discontent'
    };
    
    return dimensionMap[dimension] || null;
  };

  const isFirstDimensionValue = (dimension: string): boolean => {
    // Returns true if the dimension is the first of the pair (should be negated)
    const firstDimensions = ['theocracy', 'nationalism', 'authority', 'capitalism', 
                             'dogma', 'autocracy', 'conservatism', 'content'];
    return firstDimensions.includes(dimension);
  };

  if (isLoading) {
    return <div className="text-xs text-center">Calculating alignment...</div>;
  }

  if (error) {
    return <div className="text-xs text-center text-red-500">{error}</div>;
  }

  return null;
};

export default AlignmentDataCalculator;